/**
 * F37 (Wave 2D) — admin badge-catalog CMS page.
 *
 * Route: `/recommendation-catalog/badges`
 * Permission: `recommendation_catalog:read` (page) + `recommendation_catalog:write`
 * (Add / Edit / Delete actions — hidden when the actor lacks write).
 *
 * Trainers award these admin-managed badges to players (D6). Each entry is
 * sport-scoped (NULL = all sports — D9) and carries a design-token `icon_code`
 * (D13). Delete soft-deactivates an in-use entry instead of hard-deleting
 * (server returns 409 CATALOG_ENTRY_IN_USE — the page surfaces a notice + the
 * row flips to inactive on re-fetch).
 */
import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  Card,
  Dialog,
  EmptyState,
  Skeleton,
  Table,
  type TableColumn,
  useToast,
} from '@bravemove/ui-web';
import type { BadgeCatalogEntry, UpsertBadgeCatalogInput } from '@bravemove/types';
import { ApiError } from '../../../auth/types';
import { adminRecommendationApi } from '../../../api/recommendation';
import { translateApiError } from '../../../auth/errors';
import { useAuth } from '../../../auth/useAuth';
import { PageHeader } from '../../../components/PageHeader';
import { BadgeCatalogEditDialog } from '../../../components/recommendation/BadgeCatalogEditDialog';
import { BadgeIcon } from '../../../components/recommendation/BadgeIcon';

export function AdminBadgeCatalogPage() {
  const { t } = useI18n();
  const toast = useToast();
  const auth = useAuth();
  const canWrite = auth.adminUser?.permissions.includes('recommendation_catalog:write') ?? false;

  const [rows, setRows] = useState<readonly BadgeCatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<BadgeCatalogEntry | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BadgeCatalogEntry | null>(null);
  const [deleting, setDeleting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await adminRecommendationApi.listBadges());
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
  }, [refresh]);

  const openAdd = () => {
    setEditTarget(null);
    setEditError(null);
    setEditOpen(true);
  };
  const openEdit = (b: BadgeCatalogEntry) => {
    setEditTarget(b);
    setEditError(null);
    setEditOpen(true);
  };

  const handleSave = async (input: UpsertBadgeCatalogInput): Promise<void> => {
    setEditError(null);
    try {
      if (editTarget) {
        const { code: _omit, ...patch } = input;
        void _omit;
        await adminRecommendationApi.updateBadge(editTarget.id, patch);
      } else {
        await adminRecommendationApi.upsertBadge(input);
      }
      setEditOpen(false);
      toast.push({
        tone: 'success',
        title: t('admin:recommendation.catalog.toast_saved', { defaultValue: 'Saved' }),
      });
      await refresh();
    } catch (err) {
      setEditError(translateApiError(err, t));
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminRecommendationApi.deleteBadge(deleteTarget.id);
      setDeleteTarget(null);
      toast.push({
        tone: 'success',
        title: t('admin:recommendation.catalog.toast_deleted', { defaultValue: 'Deleted' }),
      });
      await refresh();
    } catch (err) {
      // In-use entries are soft-deactivated server-side (409). Surface a notice,
      // close the confirm, and re-fetch to show the now-inactive row.
      if (err instanceof ApiError && err.code === 'CATALOG_ENTRY_IN_USE') {
        setDeleteTarget(null);
        toast.push({
          tone: 'info',
          title: t('recommendation:catalog.in_use_notice', {
            defaultValue: 'This entry is in use and was deactivated instead of deleted.',
          }),
        });
        await refresh();
      } else {
        toast.push({
          tone: 'danger',
          title: translateApiError(err, t),
        });
      }
    } finally {
      setDeleting(false);
    }
  };

  const columns: readonly TableColumn<BadgeCatalogEntry>[] = [
    {
      key: 'icon',
      header: t('recommendation:catalog.icon_label', { defaultValue: 'Icon' }),
      cell: (row) => (
        <span className="text-ink-2">
          <BadgeIcon code={row.iconCode} size={20} />
        </span>
      ),
    },
    {
      key: 'name',
      header: t('recommendation:catalog.name_label', { defaultValue: 'Name' }),
      cell: (row) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-[12.5px] text-ink">{row.name.en ?? row.code}</span>
          <span className="font-mono text-[11px] text-ink-3">{row.code}</span>
        </div>
      ),
    },
    {
      key: 'category',
      header: t('recommendation:catalog.category_label', { defaultValue: 'Category' }),
      cell: (row) => (
        <span className="text-[12px] text-ink-2 capitalize">
          {t(`admin:recommendation.catalog.category.${row.badgeCategory}`, {
            defaultValue: row.badgeCategory,
          })}
        </span>
      ),
    },
    {
      key: 'scope',
      header: t('recommendation:catalog.sport_scope_label', { defaultValue: 'Sport scope' }),
      cell: (row) =>
        row.sportScope === null ? (
          <span className="text-[12px] text-ink-3">
            {t('recommendation:catalog.sport_scope_all', { defaultValue: 'All sports' })}
          </span>
        ) : (
          <span className="text-[12px] text-ink-2 capitalize">
            {row.sportScope.map((s) => s.replace(/-/g, ' ')).join(', ')}
          </span>
        ),
    },
    {
      key: 'active',
      header: t('recommendation:catalog.active_label', { defaultValue: 'Active' }),
      cell: (row) => (
        <Badge tone={row.active ? 'success' : 'neutral'} size="sm">
          {row.active
            ? t('admin:recommendation.catalog.active_yes', { defaultValue: 'Active' })
            : t('admin:recommendation.catalog.active_no', { defaultValue: 'Inactive' })}
        </Badge>
      ),
    },
    ...(canWrite
      ? [
          {
            key: 'actions',
            header: t('admin:recommendation.catalog.actions', { defaultValue: 'Actions' }),
            align: 'end' as const,
            cell: (row: BadgeCatalogEntry) => (
              <div className="flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openEdit(row)}
                  data-testid={`badge-edit-${row.id}`}
                >
                  {t('admin:recommendation.catalog.edit_cta', { defaultValue: 'Edit' })}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDeleteTarget(row)}
                  data-testid={`badge-delete-${row.id}`}
                >
                  {t('admin:recommendation.catalog.delete_cta', { defaultValue: 'Delete' })}
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-6" data-testid="badge-catalog-page">
      <PageHeader
        eyebrow={t('admin:recommendation.catalog.eyebrow', { defaultValue: 'Recommendations' })}
        title={t('recommendation:catalog.badges_title', { defaultValue: 'Badge catalog' })}
        subtitle={t('admin:recommendation.catalog.badges_subtitle', {
          defaultValue: 'Sport-scoped badges trainers can award to players they coach.',
        })}
        actions={
          canWrite ? (
            <Button variant="primary" onClick={openAdd} data-testid="badge-add">
              {t('admin:recommendation.catalog.add_badge', { defaultValue: 'Add badge' })}
            </Button>
          ) : null
        }
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="badge-catalog-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={t('admin:recommendation.catalog.badges_empty', {
            defaultValue: 'No badges yet — add one to get started.',
          })}
        />
      ) : (
        <Card>
          <Table
            data={rows as BadgeCatalogEntry[]}
            columns={columns as TableColumn<BadgeCatalogEntry>[]}
            rowKey={(row) => row.id}
          />
        </Card>
      )}

      <BadgeCatalogEditDialog
        open={editOpen}
        badge={editTarget}
        onSave={handleSave}
        onClose={() => setEditOpen(false)}
        errorMessage={editError}
      />

      <Dialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => void handleDelete()}
        destructive
        loading={deleting}
        title={t('admin:recommendation.catalog.badge_delete_title', {
          defaultValue: 'Delete this badge?',
        })}
        description={t('admin:recommendation.catalog.badge_delete_body', {
          defaultValue:
            'If the badge has already been awarded it will be deactivated instead of deleted.',
        })}
        confirmLabel={t('admin:recommendation.catalog.delete_cta', { defaultValue: 'Delete' })}
      />
    </div>
  );
}

export default AdminBadgeCatalogPage;
